import { useState, useCallback, useRef } from "react";
import ChatContext from "./ChatContext";
import { ChatMessage, ChatSession, ChatStreamChunk } from "../../interfaces/chat";

interface Props {
    children: JSX.Element | JSX.Element[];
}

const generateSessionId = (): string => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
};

export const ChatProvider = ({ children }: Props) => {
    const [session, setSession] = useState<ChatSession | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const abortControllerRef = useRef<AbortController | null>(null);

    const getAccessToken = (): string => {
        return window.localStorage.getItem('accessToken') || '';
    };

    const openChat = useCallback(async (projectId: number, projectTitle: string, countryCode?: string) => {
        const sessionId = generateSessionId();
        setSession({ sessionId, projectId, projectTitle, countryCode });
        setIsOpen(true);
        setIsLoading(true);
        setMessages([]);

        // Cargar historial existente
        try {
            const accessToken = getAccessToken();
            const response = await fetch(`${process.env.REACT_APP_AUTH_API_URL}/api/chat/history`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`
                },
                body: JSON.stringify({ project_id: projectId, limit: 50 })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success && data.data?.messages) {
                    setMessages(data.data.messages);
                }
            }
        } catch (error) {
            console.error('Error loading chat history:', error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const closeChat = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        setIsOpen(false);
    }, []);

    const sendMessage = useCallback(async (message: string) => {
        if (!session || !message.trim()) return;

        // Agregar mensaje del usuario
        const userMessage: ChatMessage = {
            role: 'user',
            content: message.trim(),
            timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, userMessage]);

        // Placeholder para respuesta
        const assistantPlaceholder: ChatMessage = {
            role: 'assistant',
            content: '',
            isStreaming: true
        };
        setMessages(prev => [...prev, assistantPlaceholder]);
        setIsLoading(true);

        try {
            abortControllerRef.current = new AbortController();
            const accessToken = getAccessToken();

            const response = await fetch(`${process.env.REACT_APP_AUTH_API_URL}/api/chat/send`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`,
                    'Accept': 'text/event-stream'
                },
                body: JSON.stringify({
                    project_id: session.projectId,
                    message: message.trim(),
                    session_id: session.sessionId
                }),
                signal: abortControllerRef.current.signal
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const reader = response.body?.getReader();
            const decoder = new TextDecoder();

            if (!reader) {
                throw new Error('No reader available');
            }

            let buffer = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        const data = line.slice(6).trim();
                        if (!data) continue;

                        try {
                            const parsed: ChatStreamChunk = JSON.parse(data);

                            if (parsed.error) {
                                console.error('Stream error:', parsed.error);
                                setMessages(prev => {
                                    const updated = [...prev];
                                    const lastIdx = updated.length - 1;
                                    if (updated[lastIdx]?.isStreaming) {
                                        updated[lastIdx] = {
                                            ...updated[lastIdx],
                                            content: updated[lastIdx].content || 'Lo siento, hubo un error al procesar tu mensaje.',
                                            isStreaming: false,
                                            isTruncated: parsed.truncated || false
                                        };
                                    }
                                    return updated;
                                });
                                continue;
                            }

                            if (parsed.content) {
                                setMessages(prev => {
                                    const updated = [...prev];
                                    const lastIdx = updated.length - 1;
                                    updated[lastIdx] = {
                                        ...updated[lastIdx],
                                        content: updated[lastIdx].content + parsed.content
                                    };
                                    return updated;
                                });
                            }

                            if (parsed.done) {
                                setMessages(prev => {
                                    const updated = [...prev];
                                    const lastIdx = updated.length - 1;
                                    updated[lastIdx] = {
                                        ...updated[lastIdx],
                                        isStreaming: false,
                                        timestamp: new Date().toISOString()
                                    };
                                    return updated;
                                });
                            }
                        } catch {
                            // Ignorar errores de parsing
                        }
                    }
                }
            }

            reader.releaseLock();

            // Safety: ensure streaming is marked complete even if no {done} event was received
            setMessages(prev => {
                const updated = [...prev];
                const lastIdx = updated.length - 1;
                if (lastIdx >= 0 && updated[lastIdx].isStreaming) {
                    updated[lastIdx] = {
                        ...updated[lastIdx],
                        isStreaming: false,
                        isTruncated: updated[lastIdx].content.length > 0,
                        timestamp: new Date().toISOString()
                    };
                }
                return updated;
            });

        } catch (error: any) {
            if (error.name === 'AbortError') {
                console.log('Request aborted');
            } else {
                console.error('Error sending message:', error);
                setMessages(prev => {
                    const updated = [...prev];
                    const lastIdx = updated.length - 1;
                    if (lastIdx >= 0 && updated[lastIdx].isStreaming) {
                        const hasContent = updated[lastIdx].content.length > 0;
                        updated[lastIdx] = {
                            ...updated[lastIdx],
                            content: hasContent
                                ? updated[lastIdx].content
                                : 'Lo siento, hubo un error al procesar tu mensaje.',
                            isStreaming: false,
                            isTruncated: hasContent
                        };
                    }
                    return updated;
                });
            }
        } finally {
            setIsLoading(false);
        }
    }, [session]);

    const retryLastMessage = useCallback(async () => {
        if (!session || messages.length < 2) return;

        // Buscar el ultimo mensaje del usuario
        const lastUserMsg = [...messages].reverse().find(m => m.role === 'user');
        if (!lastUserMsg) return;

        // Remover el mensaje truncado del assistant Y el mensaje del usuario
        // (sendMessage los vuelve a agregar)
        setMessages(prev => {
            let result = prev;
            // Remover assistant truncado
            if (result.length > 0 && result[result.length - 1].role === 'assistant') {
                result = result.slice(0, result.length - 1);
            }
            // Remover el user message (sendMessage lo re-agrega)
            if (result.length > 0 && result[result.length - 1].role === 'user') {
                result = result.slice(0, result.length - 1);
            }
            return result;
        });

        // Re-enviar el mismo mensaje
        await sendMessage(lastUserMsg.content);
    }, [session, messages, sendMessage]);

    const clearHistory = useCallback(async () => {
        if (!session) return;

        try {
            const accessToken = getAccessToken();
            await fetch(`${process.env.REACT_APP_AUTH_API_URL}/api/chat/clear`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`
                },
                body: JSON.stringify({ project_id: session.projectId })
            });
            setMessages([]);
        } catch (error) {
            console.error('Error clearing history:', error);
        }
    }, [session]);

    return (
        <ChatContext.Provider value={{
            session,
            isOpen,
            isLoading,
            messages,
            openChat,
            closeChat,
            sendMessage,
            clearHistory,
            retryLastMessage
        }}>
            {children}
        </ChatContext.Provider>
    );
};

export default ChatProvider;
